import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'

const RegisterPage = () => {
  const { t } = useTranslation('auth')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { register: registerUser, isLoading } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch
  } = useForm()

  const password = watch('password')

  const onSubmit = async (data) => {
    const { confirmPassword, ...userData } = data

    const result = await registerUser(userData)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setError('root', { message: result.error })
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-bg)' }}>
      {/* Left side — illustration */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-8" style={{ background: 'var(--color-surface)' }}>
        <img
          src="/auth-illustration.png"
          alt=""
          className="w-full object-contain px-4"
          style={{ maxHeight: '85vh' }}
        />
      </div>

      {/* Right side — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1f9f93] p-2" style={{ boxShadow: '0 10px 22px rgba(31,159,147,.22)' }}>
              <img src="/logo.png" alt="Finomi" className="w-10 h-10 object-contain brightness-0 invert" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-[var(--color-text)]">
            {t('register.title')}
          </h2>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            {t('register.subtitle')}
          </p>
        </div>

        {/* Register form */}
        <Card className="mt-8">
          <Card.Body>
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* First Name */}
              <div>
                <label htmlFor="first_name" className="form-label">
                  {t('register.firstName')}
                </label>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  className="form-input"
                  placeholder={t('register.firstNamePlaceholder')}
                  {...register('first_name', {
                    required: t('validation:firstNameRequired'),
                    minLength: {
                      value: 2,
                      message: t('validation:firstNameMinLength')
                    }
                  })}
                />
                {errors.first_name && (
                  <p className="form-error">{errors.first_name.message}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="last_name" className="form-label">
                  {t('register.lastName')}
                </label>
                <input
                  id="last_name"
                  type="text"
                  autoComplete="family-name"
                  className="form-input"
                  placeholder={t('register.lastNamePlaceholder')}
                  {...register('last_name', {
                    required: t('validation:lastNameRequired'),
                    minLength: {
                      value: 2,
                      message: t('validation:lastNameMinLength')
                    }
                  })}
                />
                {errors.last_name && (
                  <p className="form-error">{errors.last_name.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="form-label">
                  {t('register.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="form-input"
                  placeholder={t('register.emailPlaceholder')}
                  {...register('email', {
                    required: t('validation:emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('validation:emailInvalid')
                    }
                  })}
                />
                {errors.email && (
                  <p className="form-error">{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="form-label">
                  {t('register.passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="form-input pr-10"
                    placeholder={t('register.passwordPlaceholder')}
                    {...register('password', {
                      required: t('validation:passwordRequired'),
                      minLength: {
                        value: 8,
                        message: t('validation:passwordMinLength')
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: t('validation:passwordPattern')
                      }
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="form-label">
                  {t('register.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="form-input pr-10"
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    {...register('confirmPassword', {
                      required: t('validation:confirmPasswordRequired'),
                      validate: value =>
                        value === password || t('validation:passwordsMismatch')
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="form-error">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Language */}
              <div>
                <label htmlFor="language" className="form-label">
                  {t('register.language')}
                </label>
                <select
                  id="language"
                  className="form-select"
                  {...register('language')}
                >
                  <option value="pl">{t('register.langPl')}</option>
                  <option value="en">{t('register.langEn')}</option>
                </select>
              </div>

              {/* Terms */}
              <div className="flex items-center">
                <input
                  id="terms"
                  type="checkbox"
                  className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  {...register('terms', {
                    required: t('validation:termsRequired')
                  })}
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-[var(--color-text)]">
                  {t('register.terms')}{' '}
                  <Link
                    to="/terms"
                    className="text-[#1f9f93] hover:text-[#167e75]"
                  >
                    {t('register.termsLink')}
                  </Link>{' '}
                  {t('register.and', 'i')}{' '}
                  <Link
                    to="/privacy"
                    className="text-[#1f9f93] hover:text-[#167e75]"
                  >
                    {t('register.privacyLink')}
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <p className="form-error">{errors.terms.message}</p>
              )}

              {/* Error message */}
              {errors.root && (
                <div className="rounded-md bg-error-50 p-4">
                  <p className="text-sm text-error-800">{errors.root.message}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                className="w-full"
              >
                {t('register.submit')}
              </Button>
            </form>
          </Card.Body>
        </Card>

        {/* Login link */}
        <div className="text-center">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('register.hasAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-[#1f9f93] hover:text-[#167e75]"
            >
              {t('register.loginLink')}
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  )
}

export default RegisterPage
